<template>
  <div>
    <h3 class="text-2xl max-w-2xl text-vice-teal-900 font-semibold leading-tight">
      Attendance per participants
    </h3>
    <div class="relative overflow-x-auto shadow-md border rounded-lg mt-5 mb-10">
      <table class="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
        <thead
          class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400"
        >
          <tr>
            <th scope="col" class="px-6 py-3">Name</th>
            <th scope="col" class="px-6 py-3">Role</th>
            <th scope="col" class="px-6 py-3">Status</th>
            <th scope="col" class="px-6 py-3">Present</th>
            <th scope="col" class="px-6 py-3">Reflections</th>
            <th scope="col" class="px-6 py-3">Proof of attendance</th>
            <!-- <th></th> -->
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="invitation in attendanceData"
            :key="invitation.name"
            class="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
          >
            <th
              scope="row"
              class="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
            >
              {{ invitation.name }}
            </th>
            <td class="px-6 py-4 whitespace-nowrap">{{ invitation.role }}</td>
            <td class="px-6 py-4 whitespace-nowrap">{{ invitation.status }}</td>
            <td class="px-6 py-4 whitespace-nowrap">{{ invitation.present }}</td>
            <td class="px-6 py-4 whitespace-nowrap">
              <ViceBadge
                v-if="invitation.reflection"
                small
                class="
                  bg-vice-teal-500
                  text-white
                  cursor-pointer
                  hover:bg-vice-teal-600
                  py-0.5
                  pl-2
                  pr-2
                  select-none
                "
                @click="reflection = invitation.reflection"
              >
                Show
              </ViceBadge>
              <span v-else class="text-gray-700 px-5 select-none">–</span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="flex flex-row md:flex-row gap-1 justify-between items-center">
                <div class="flex flex-row gap-1 whitespace-nowrap">
                  <div
                    v-if="invitation.proofOfAttendance.status === attendanceStatusPresent"
                    class="flex flex-row"
                  >
                    <ViceBadge small class="gb-blue text-blue p-0.5">
                      {{ invitation.proofOfAttendance.status }}
                    </ViceBadge>
                  </div>

                  <div
                    v-else-if="
                      invitation.proofOfAttendance.status === attendanceStatusNeedsVerification
                    "
                    class="flex flex-row"
                  >
                    <ViceBadge small class="bg-amber-600 text-white p-0.5">
                      {{ invitation.proofOfAttendance.status }}
                    </ViceBadge>
                  </div>

                  <div
                    v-else-if="invitation.proofOfAttendance.status === attendanceStatusNotPresent"
                    class="flex flex-row"
                  >
                    <ViceBadge small class="bg-red-600 text-white p-0.5">
                      {{ invitation.proofOfAttendance.status }}
                    </ViceBadge>
                  </div>

                  <div v-if="invitation.proofOfAttendance.proofGenerated" class="flex flex-row">
                    <ViceBadge small class="gb-blue text-blue p-0.5">Proof generated</ViceBadge>
                  </div>
                </div>
                <div v-if="invitation.proofOfAttendance.markAsPresentLink">
                  <button
                    :href="invitation.proofOfAttendance.markAsPresentLink.url"
                    @click="
                      markAsPresent(
                        event,
                        invitation.proofOfAttendance.markAsPresentLink.session_id,
                        invitation.proofOfAttendance.markAsPresentLink.user_id,
                      )
                    "
                    class="
                      bg-white
                      hover:bg-gray-200
                      ring-gray-300 ring-1
                      rounded-md
                      text-sm
                      px-2
                      py-1
                      focus:outline-none
                      text-gray-600
                    "
                  >
                    Mark as present
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <TransitionRoot as="template" :show="!!reflection?.id">
    <Dialog as="div" class="fixed z-10 zoom-80 inset-0 overflow-y-auto" @close="reflection = null">
      <div
        class="
          flex
          items-end
          justify-center
          min-h-screen
          pt-4
          px-4
          pb-20
          text-center
          sm:block
          sm:p-0
        "
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="
              inline-block
              align-bottom
              bg-white
              rounded-lg
              px-4
              pt-5
              pb-4
              text-left
              overflow-hidden
              shadow-xl
              transform
              transition-all
              sm:my-8
              sm:align-middle
              sm:max-w-4xl
              sm:w-full
              sm:p-6
            "
          >
            <div>
              <h3 class="text-lg leading-6 font-medium text-gray-900">Reflection</h3>
              <div class="mt-4 text-sm text-gray-500">
                <span class="text-xxs text-gray-700">What:</span>
                <p class="mx-4 mb-4">{{ reflection?.what }}</p>
                <span class="text-xxs text-gray-700">So what:</span>
                <p class="mx-4 mb-4">{{ reflection?.so_what }}</p>
                <span class="text-xxs text-gray-700">Now what:</span>
                <p class="mx-4">{{ reflection?.now_what }}</p>
              </div>
              <div class="flex justify-end">
                <ViceButton white medium @click="reflection = null">Close</ViceButton>
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import axios from 'axios';
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from '@headlessui/vue';
import ViceBadge from '@/Components/Badges/ViceBadge.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';

export default {
  name: 'AttendancePerParticipants',
  props: {
    attendancePerParticipantsData: {
      require: true,
    },
  },
  components: {
    ViceButton,
    DialogOverlay,
    TransitionChild,
    Dialog,
    TransitionRoot,
    ViceBadge,
  },
  created() {
    this.getAttendanceData();
  },
  data() {
    return {
      reflection: null,
      attendanceData: [],
      attendanceStatusPresent: 'Present',
      attendanceStatusNotPresent: 'Not present',
      attendanceStatusNeedsVerification: 'Needs verification',
    };
  },
  methods: {
    async markAsPresent(event, sessionId, userId) {
      const requestData = {
        session_id: sessionId,
        user_id: userId,
      };

      const response = await axios.post(route('session.clinician.mark-as-present'), requestData);

      this.attendanceData = await response.data;
    },
    async getAttendanceData() {
      const response = await axios.get(route('get-attendance-data', this.$page.props.session.id));
      this.attendanceData = response.data;
    },
  },
};
</script>

<style scoped>
.bg-amber-600 {
  background-color: #ea580c;
}

.gb-blue {
  background-color: #99f6e4;
}

.text-blue {
  color: #5a1bb5;
}

.icon-color {
  color: #727373;
}
</style>
